/**
 * BrandingPreview — the live "Vorschau" of a broker-branded customer email AND a
 * broker-branded portal page (Wave W4 — branding, ui+preview).
 *
 * A PURE, client-side render of representative markup using the chosen logo + three
 * colours + broker name. It faithfully mirrors the api email-template's structure and
 * FIXED tokens (pageBg / card / ink / muted / border / navy) so the operator sees the
 * exact effect of a colour before it is patched: the header band uses headerColor, the
 * CTA uses primaryColor (contrast-picked label), the highlight/badge uses secondaryColor,
 * while body text / card / borders stay FIXED for readability.
 *
 * Broker hex values flow in as INLINE styles (never Tailwind classes) — so a broker's
 * colour can never trip the admin brand-guard (no emerald/green Tailwind), and the
 * preview matches the sent mail regardless of the Brokly palette.
 *
 * The logo <img> degrades to the broker-name wordmark on load error / when absent —
 * exactly like the email template's wordmark fallback.
 *
 * [Source: apps/api/src/mailer/email-template.ts] (email structure + FIXED tokens)
 */

import { useEffect, useState } from 'react';
import type { BrokerBrandingColors } from '@brokly/shared';
import { FIXED_TOKENS, PREVIEW_LEGAL_LINE, readableInkOn, resolvePreviewColors } from './broker-colors';

export function BrandingPreview({
  name,
  logoUrl,
  colors,
}: {
  /** Broker display name — the wordmark + heading personalisation. */
  name: string;
  /** Logo URL (object-URL of a freshly-picked file, or the stored public logo). */
  logoUrl?: string | null;
  colors: BrokerBrandingColors;
}) {
  const { primaryColor, secondaryColor, headerColor } = resolvePreviewColors(colors);
  const brokerName = name.trim() === '' ? 'Ihr Broker' : name.trim();

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div>
        <p className="mb-1.5 text-xs font-medium text-muted-foreground">Kunden-E-Mail</p>
        <EmailPreview
          brokerName={brokerName}
          logoUrl={logoUrl}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          headerColor={headerColor}
        />
      </div>
      <div>
        <p className="mb-1.5 text-xs font-medium text-muted-foreground">Kundenportal</p>
        <PortalPreview
          brokerName={brokerName}
          logoUrl={logoUrl}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          headerColor={headerColor}
        />
      </div>
    </div>
  );
}

/**
 * A logo <img> that falls back to a text wordmark on error / when there is no URL —
 * mirrors the email template's "no logo → broker name as wordmark" behaviour.
 */
function Wordmarkable({
  logoUrl,
  brokerName,
  color,
  maxHeight,
}: {
  logoUrl?: string | null;
  brokerName: string;
  color: string;
  maxHeight: number;
}) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [logoUrl]);

  if (logoUrl && !failed) {
    return (
      <img
        src={logoUrl}
        alt={brokerName}
        onError={() => setFailed(true)}
        style={{ display: 'block', height: 'auto', maxHeight, maxWidth: 160 }}
      />
    );
  }
  return (
    <span style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2, color }}>{brokerName}</span>
  );
}

function EmailPreview({
  brokerName,
  logoUrl,
  primaryColor,
  secondaryColor,
  headerColor,
}: {
  brokerName: string;
  logoUrl?: string | null;
  primaryColor: string;
  secondaryColor: string;
  headerColor: string;
}) {
  const headerInk = readableInkOn(headerColor);
  const ctaInk = readableInkOn(primaryColor);
  return (
    // The page-bg frame the email card sits on (FIXED).
    <div
      style={{
        backgroundColor: FIXED_TOKENS.pageBg,
        borderRadius: 8,
        padding: 16,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          maxWidth: 420,
          margin: '0 auto',
          backgroundColor: FIXED_TOKENS.card,
          border: `1px solid ${FIXED_TOKENS.border}`,
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        {/* Header band — headerColor behind the broker logo/wordmark. */}
        <div style={{ backgroundColor: headerColor, padding: '16px 20px' }}>
          <Wordmarkable
            logoUrl={logoUrl}
            brokerName={brokerName}
            color={headerInk}
            maxHeight={36}
          />
        </div>
        {/* Body — FIXED navy heading + ink paragraphs. */}
        <div style={{ padding: '16px 20px 8px' }}>
          <h4
            style={{
              margin: '0 0 10px',
              fontSize: 16,
              fontWeight: 700,
              color: FIXED_TOKENS.navy,
            }}
          >
            Ihre Police wurde erneuert
          </h4>
          <p style={{ margin: '0 0 12px', fontSize: 13, lineHeight: 1.6, color: FIXED_TOKENS.ink }}>
            Guten Tag, Ihre Versicherungspolice wurde soeben erfolgreich erneuert. Die Details
            finden Sie in Ihrem Kundenportal.
          </p>
          {/* Highlight block — secondaryColor accent trim. */}
          <div
            style={{
              margin: '0 0 14px',
              padding: '10px 12px',
              backgroundColor: FIXED_TOKENS.pageBg,
              border: `1px solid ${FIXED_TOKENS.border}`,
              borderLeft: `4px solid ${secondaryColor}`,
              borderRadius: 6,
              fontSize: 13,
              color: FIXED_TOKENS.ink,
            }}
          >
            Police-Nr. <strong>POL-2026-00841</strong>
          </div>
          {/* Primary CTA — primaryColor fill, contrast-picked label. */}
          <span
            style={{
              display: 'inline-block',
              padding: '9px 20px',
              backgroundColor: primaryColor,
              color: ctaInk,
              fontSize: 13,
              fontWeight: 600,
              borderRadius: 8,
            }}
          >
            Im Portal ansehen
          </span>
        </div>
        {/* Footer — FIXED muted legal line. */}
        <div style={{ padding: '16px 20px 20px' }}>
          <div style={{ borderTop: `1px solid ${FIXED_TOKENS.border}`, marginBottom: 12 }} />
          <p style={{ margin: 0, fontSize: 11, lineHeight: 1.5, color: FIXED_TOKENS.muted }}>
            {brokerName} · {PREVIEW_LEGAL_LINE}
          </p>
        </div>
      </div>
    </div>
  );
}

function PortalPreview({
  brokerName,
  logoUrl,
  primaryColor,
  secondaryColor,
  headerColor,
}: {
  brokerName: string;
  logoUrl?: string | null;
  primaryColor: string;
  secondaryColor: string;
  headerColor: string;
}) {
  const headerInk = readableInkOn(headerColor);
  const primaryInk = readableInkOn(primaryColor);
  const secondaryInk = readableInkOn(secondaryColor);
  return (
    <div
      style={{
        backgroundColor: FIXED_TOKENS.pageBg,
        border: `1px solid ${FIXED_TOKENS.border}`,
        borderRadius: 10,
        overflow: 'hidden',
      }}
    >
      {/* Top bar — headerColor band behind the broker logo/wordmark. */}
      <div
        style={{
          backgroundColor: headerColor,
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Wordmarkable logoUrl={logoUrl} brokerName={brokerName} color={headerInk} maxHeight={28} />
        <span style={{ fontSize: 12, color: headerInk, opacity: 0.85 }}>Kundenportal</span>
      </div>
      {/* Nav — active item uses primaryColor. */}
      <div
        style={{
          display: 'flex',
          gap: 4,
          padding: '10px 16px 0',
          borderBottom: `1px solid ${FIXED_TOKENS.border}`,
        }}
      >
        {['Policen', 'Rechnungen', 'Nachrichten'].map((tab, i) => (
          <span
            key={tab}
            style={{
              padding: '6px 10px',
              fontSize: 12,
              fontWeight: 600,
              color: i === 0 ? primaryColor : FIXED_TOKENS.muted,
              borderBottom: i === 0 ? `2px solid ${primaryColor}` : '2px solid transparent',
            }}
          >
            {tab}
          </span>
        ))}
      </div>
      {/* Content — FIXED white card, ink text, secondaryColor badge, primaryColor button. */}
      <div style={{ padding: 16 }}>
        <div
          style={{
            backgroundColor: FIXED_TOKENS.card,
            border: `1px solid ${FIXED_TOKENS.border}`,
            borderRadius: 8,
            padding: 14,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 8,
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 700, color: FIXED_TOKENS.ink }}>
              Hausrat &amp; Haftpflicht
            </span>
            <span
              style={{
                padding: '2px 8px',
                fontSize: 11,
                fontWeight: 600,
                borderRadius: 999,
                backgroundColor: secondaryColor,
                color: secondaryInk,
              }}
            >
              Aktiv
            </span>
          </div>
          <p style={{ margin: '0 0 12px', fontSize: 12, lineHeight: 1.5, color: FIXED_TOKENS.muted }}>
            Police-Nr. POL-2026-00841 · Prämie CHF 480/Jahr
          </p>
          <span
            style={{
              display: 'inline-block',
              padding: '7px 16px',
              backgroundColor: primaryColor,
              color: primaryInk,
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 8,
            }}
          >
            Dokumente ansehen
          </span>
        </div>
      </div>
    </div>
  );
}
